import React from 'react';
import i18next from 'i18next';
import { useFormContext, Controller } from 'react-hook-form';
import _ from 'lodash';

import CalculatorSelectInput from 'containers/administration/calculator/form/calculatorInputs/CalculatorSelectInput';
import CalculatorStandardInput from 'containers/administration/calculator/form/calculatorInputs/CalculatorStandardInput';
import EAInputBloc from 'components/common/EAInputBloc';
import EADatePicker from 'components/common/EADatePicker';
import {
  FieldType,
  Operator,
  REGEXP_DECIMAL,
  ThirdpartyScoreType,
} from 'services/common/Constants';
import CalculatorMultipleValueInput from 'containers/administration/calculator/form/calculatorInputs/CalculatorMultipleValueInput';
import DateCriterionValue from './criterionValues/dateCriterionValue';
import NumericValue from './criterionValues/numericValue';
import TextValue from './criterionValues/textValue';
import BooleanValue from './criterionValues/booleanValue';
import AttributeValue from './criterionValues/attributeValue';
import CalculatedValue from './criterionValues/calculatedValue';
import FormValue from './criterionValues/formValue';
import OcrExtractedValue from './criterionValues/ocrExtractedValue';

const CriterionValues = ({
  fieldName,
  showLabel,
  selectedFieldType,
  selectedFieldSubType,
  selectedComplexListSubFieldType,
  selectedOperator,
  availableValueOptions,
  variantAutoComplete,
  readOnly = false
}) => {
  const formMethods = useFormContext();
  const { setError } = formMethods;

  switch (selectedFieldType) {
    case FieldType.TEXT:
    case FieldType.ACTIVITY:
      return (
        <TextValue
          selectedOperator={selectedOperator}
          selectedFieldSubType={selectedFieldSubType}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          variantAutoComplete={variantAutoComplete}
          readOnly={readOnly}
        />
      );
    case FieldType.NUMERIC:
      return (
        <NumericValue
          selectedOperator={selectedOperator}
          selectedFieldSubType={selectedFieldSubType}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          readOnly={readOnly}
        />
      );
    case FieldType.DATE:
      return (
        <DateCriterionValue
          selectedOperator={selectedOperator}
          fieldName={fieldName}
          showLabel={showLabel}
          readOnly={readOnly}
        />
      );
    case FieldType.BOOLEAN:
    case FieldType.CUSTOM:
    case FieldType.RISK:
      if (selectedFieldSubType === ThirdpartyScoreType.CREDITSAFE_NUMERIC) {
        return (
          <NumericValue
            selectedOperator={selectedOperator}
            selectedFieldSubType={selectedFieldSubType}
            fieldName={fieldName}
            showLabel={showLabel}
            availableValueOptions={availableValueOptions}
            readOnly={readOnly}
          />
        );
      } else {
        return (
        <BooleanValue
          selectedOperator={selectedOperator}
          selectedFieldSubType={selectedFieldSubType}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          readOnly={readOnly}
        />
      );
    }
    case FieldType.ATTRIBUTE:
    case FieldType.CONTEXT_RANK:
      return (
        <AttributeValue
          selectedFieldSubType={selectedFieldSubType}
          selectedOperator={selectedOperator}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          readOnly={readOnly}
        />
      );
    case FieldType.SOURCE_DATA:
      return (
        <>
          {(() => {
            switch (selectedFieldSubType) {
              case FieldType.TEXT:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                          return !_.isEmpty(availableValueOptions) ? (
                            <CalculatorSelectInput
                              fieldName={`${fieldName}.${0}`}
                              label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                              options={availableValueOptions}
                              isRequired
                              validate={() => {
                                return null;
                              }}
                              uxVariant="criteriaInput"
                              variant="standard"
                              labelVariant="chip"
                              triggerOnContinue
                              disabled={readOnly}
                            />
                          ) : (
                            <CalculatorStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired={true}
                              label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.indicateValue`)}
                              uxVariant="criteriaInput"
                              variant="standard"
                              triggerOnContinue
                              validate={() => {
                                return null;
                              }}
                              disabled={readOnly}
                            />
                          );

                        case Operator.CONTAINS:
                        case Operator.NOT_CONTAINS:
                        case Operator.STARTS_WITH:
                        case Operator.NOT_STARTS_WITH:
                        case Operator.ENDS_WITH:
                        case Operator.NOT_ENDS_WITH:
                          return (
                            <CalculatorStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired={true}
                              label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.indicateValue`)}
                              uxVariant="criteriaInput"
                              variant="standard"
                              validate={() => {
                                return null;
                              }}
                              disabled={readOnly}
                              triggerOnContinue
                            />
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (<></>);

                        /* case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return !_.isEmpty(availableValueOptions) ? (
                            <CalculatorMultipleSelectInput
                              fieldName={`${fieldName}`}
                              isRequired
                              label={`${showLabel? i18next.t("calculator.dialog.create.label.values") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                              options={availableValueOptions}
                              uxVariant="criteriaInput"
                              variant="standard"
                              validate={(value) => {
                                if (value.length === 0) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t(`calculator.errors.criterionValueEmpty`)
                                  });
                                  return false;
                                } else {
                                  clearErrors(`${fieldName}`);
                                  return true;
                                }
                              }}
                            />
                          ) : (
                            <CalculatorMultipleValueInput
                              fieldName={`${fieldName}`}
                              isRequired
                              label={`${showLabel? i18next.t("calculator.dialog.create.label.values") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                              uxVariant="criteriaInput"
                              validate={(value) => {
                                if (value.length === 0) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t(`calculator.errors.criterionValueEmpty`)
                                  });
                                  return false;
                                } else {
                                  clearErrors(`${fieldName}`);
                                  return true;
                                }
                              }}
                            />
                          );
 */
                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              case FieldType.NUMERIC:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return !_.isEmpty(availableValueOptions) ? (
                              <CalculatorSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                                placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                                options={availableValueOptions}
                                uxVariant="criteriaInput"
                                variant="standard"
                                isRequired
                                validate={(value) => {
                                  return value && !value.match(REGEXP_DECIMAL)
                                    ? i18next.t("attributes.errors.decimal")
                                    : null;
                                }}
                                labelVariant="chip"
                                triggerOnContinue
                                disabled={readOnly}
                              />
                            ) : (
                              <CalculatorStandardInput
                                fieldName={`${fieldName}.${0}`}
                                isRequired
                                uxVariant="criteriaInput"
                                variant="standard"
                                label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                                placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                                triggerOnContinue
                                validate={(value) => {
                                  return value && !value.match(REGEXP_DECIMAL)
                                    ? i18next.t("attributes.errors.decimal")
                                    : null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.GREATER_THAN:
                          case Operator.GREATER_OR_EQUAL:
                          case Operator.LESS_THAN:
                          case Operator.LESS_OR_EQUAL:
                            return (
                              <CalculatorStandardInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                                placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                                options={availableValueOptions}
                                uxVariant="criteriaInput"
                                variant="standard"
                                isRequired
                                triggerOnContinue
                                validate={(value) => {
                                  return value && !value.match(REGEXP_DECIMAL)
                                    ? i18next.t("attributes.errors.decimal")
                                    : null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.BETWEEN:
                            return (
                              <>
                                {/*TODO: Two values*/}
                              </>
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                              </>
                            );

                          case Operator.IN_SET:
                          case Operator.NOT_IN_SET:
                            return !_.isEmpty(availableValueOptions) ? (
                              <CalculatorSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                                placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                                options={availableValueOptions}
                                uxVariant="criteriaInput"
                                variant="standard"
                                isRequired
                                triggerOnContinue
                                validate={(value) => {
                                  let isValid = true;
                                  if (value && !value.match(REGEXP_DECIMAL)) {
                                    setError(`${fieldName}`, {
                                      type: 'manual',
                                      message: i18next.t("attributes.errors.decimal")
                                    });

                                    isValid = false;
                                  }
                                  return isValid;
                                }}
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            ) : (
                              <CalculatorMultipleValueInput
                                fieldName={`${fieldName}`}
                                isRequired
                                label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                                uxVariant="criteriaInput"
                                placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                                triggerOnContinue
                                disabled={readOnly}
                                validate={(value) => {
                                  let isValid = true;
                                  if (value && !value.match(REGEXP_DECIMAL)) {
                                    setError(`${fieldName}`, {
                                      type: 'manual',
                                      message: i18next.t("attributes.errors.decimal")
                                    });

                                    isValid = false;
                                  }


                                  return isValid;
                                }}
                              />
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case FieldType.BOOLEAN:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return (
                              <CalculatorSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`}
                                placeholder={i18next.t(`calculator.dialog.create.placeholder.selectValues`)}
                                options={[
                                  { value: "true", label: i18next.t("yes") },
                                  { value: "false", label: i18next.t("no") },
                                ]}
                                isRequired
                                validate={() => {
                                  return null;
                                }}
                                uxVariant="criteriaInput"
                                variant="standard"
                                labelVariant="chip"
                                triggerOnContinue
                                disabled={readOnly}
                              />
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                              </>
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case FieldType.DATE:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                        case Operator.GREATER_THAN:
                        case Operator.GREATER_OR_EQUAL:
                        case Operator.LESS_THAN:
                        case Operator.LESS_OR_EQUAL:
                          return (
                            <EAInputBloc label={`${showLabel ? i18next.t("calculator.dialog.create.label.values") : ""}`} customLabelStyle={{ fontSize: "14px" }}>
                              <Controller
                                control={formMethods.control}
                                name={`${fieldName}.${0}`}
                                rules={{
                                  required: i18next.t(`infos.required`),
                                  validate: (value) => {
                                    if (!value) {
                                      return i18next.t(`infos.required`);
                                    }
                                    // Add more custom validation logic if needed
                                    return true;
                                  }
                                }}
                                render={({ field }) => (
                                  <EADatePicker
                                    {...field}
                                    name={field.name}
                                    formMethods={formMethods}
                                    disableHighlightToday
                                    ref={null}
                                    isRequired
                                    triggerOnContinue
                                    disabled={readOnly}
                                  />
                                )}
                              />
                            </EAInputBloc>
                          );


                        case Operator.BETWEEN:
                          return (
                            <>
                              {/*TODO*/}
                            </>
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (<></>);

                        case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return (
                            <>
                              {/*TODO*/}
                            </>
                          );

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              default:
                break;
            }
          })()}
        </>
      );

    case FieldType.CALCULATED_ATTRIBUTE:
    case FieldType.INDICATOR:
    case FieldType.SOURCE_VARIABLE:
      return (
        <CalculatedValue
          selectedFieldSubType={selectedFieldSubType}
          selectedOperator={selectedOperator}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          readOnly={readOnly}
        />
      );

    case FieldType.FORM_FIELD:
      return (
        <FormValue
          selectedFieldSubType={selectedFieldSubType}
          selectedComplexListSubFieldType={selectedComplexListSubFieldType}
          selectedOperator={selectedOperator}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          readOnly={readOnly}
        />
      )

    case FieldType.OCR_EXTRACTED_FIELD:
      return (
        <OcrExtractedValue
          selectedFieldSubType={selectedFieldSubType}
          selectedOperator={selectedOperator}
          fieldName={fieldName}
          showLabel={showLabel}
          availableValueOptions={availableValueOptions}
          readOnly={readOnly}
        />
      )
    default:
      return <></>;
  }
};

export default CriterionValues;
